import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fcfa } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Smartphone, CreditCard } from "lucide-react";

const METHODS = [
  { key: "orange_money", label: "Orange Money", color: "#FF6B00" },
  { key: "mtn", label: "MTN MoMo", color: "#FFCC00" },
  { key: "wave", label: "Wave", color: "#1DC8FF" },
  { key: "moov", label: "Moov Money", color: "#0066B3" },
  { key: "card", label: "Carte bancaire", color: "#1A2B4C" },
];

export default function Payment() {
  const { txId } = useParams();
  const navigate = useNavigate();
  const [tx, setTx] = useState(null);
  const [method, setMethod] = useState("orange_money");
  const [phone, setPhone] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments").then((r) => setTx(r.data.find((t) => t.tx_id === txId) || null));
  }, [txId]);

  const pay = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/${txId}/confirm`);
      setDone(true);
      toast.success("Paiement confirmé !");
    } catch { toast.error("Échec du paiement"); }
    finally { setBusy(false); }
  };

  if (!tx) return <div className="text-center py-20 text-muted-foreground">Chargement…</div>;

  if (done)
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center animate-fade-up">
        <CheckCircle2 className="w-20 h-20 text-accent mx-auto mb-4" />
        <h1 className="font-display text-2xl font-bold text-secondary">Paiement réussi</h1>
        <p className="text-muted-foreground mt-2">{fcfa(tx.amount)} — {tx.kind === "abonnement" ? "Abonnement Pro activé" : "Prestation payée"}.</p>
        <Button className="mt-6 bg-primary hover:bg-primary/90" onClick={() => navigate("/dashboard")}>Retour à mon espace</Button>
      </div>
    );

  return (
    <div className="max-w-md mx-auto px-6 py-10">
      <div className="bg-card border border-border rounded-2xl p-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full mb-3">
            {tx.mock ? "MODE SIMULÉ (MOCK)" : "CinetPay"}
          </div>
          <div className="text-sm text-muted-foreground">Montant à payer</div>
          <div className="font-display text-4xl font-extrabold text-secondary">{fcfa(tx.amount)}</div>
          <div className="text-xs text-muted-foreground capitalize mt-1">{tx.kind}</div>
        </div>

        <div className="space-y-2 mb-4">
          {METHODS.map((m) => (
            <button key={m.key} data-testid={`method-${m.key}`} onClick={() => setMethod(m.key)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${method === m.key ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border"}`}>
              <span className="w-8 h-8 rounded-md flex items-center justify-center text-white" style={{ background: m.color }}>
                {m.key === "card" ? <CreditCard className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
              </span>
              <span className="font-medium text-secondary">{m.label}</span>
            </button>
          ))}
        </div>

        {method !== "card" && (
          <input data-testid="pay-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Numéro Mobile Money"
            className="w-full border border-input rounded-lg px-3 py-2 mb-4 text-sm" />
        )}

        <Button data-testid="pay-confirm" className="w-full bg-primary hover:bg-primary/90 h-11" onClick={pay} disabled={busy}>
          {busy ? "Traitement…" : `Payer ${fcfa(tx.amount)}`}
        </Button>
        <p className="text-[11px] text-muted-foreground text-center mt-3">
          Paiement en mode simulé. Intégration CinetPay réelle prête à brancher (clés API requises).
        </p>
      </div>
    </div>
  );
}
